// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, ImprRadio, ImprTooltip, InputBox } from '@imprivata-cloud/components';
import type { FormInstance } from 'antd';
import { Alert, Checkbox, ConfigProvider, Form, Input, Radio } from 'antd';
import { AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { IdvConnection } from '../../../api/types';
import { IdvAuthMethod, IdvConnectionTemplate, IdvIalLevel } from '../../../api/types';
import CopyButton from '../../../components/copyButton/copyButton';
import { copyToClipboard } from '../../../utils/utils';
import { FieldName, type FormValues, TRANSLATION_ROOT } from './IdvConnections.types';

type TFunction = ReturnType<typeof useTranslation>['t'];

// Humanize a scope id we ship no copy for (e.g. one the tenant's IdP advertises that we don't
// have i18n for): "address" → "Address". Used as the checkbox title fallback so a raw i18n key
// is never shown; such scopes render with a title but no description line.
const humanizeScope = (scope: string): string => scope.charAt(0).toUpperCase() + scope.slice(1);

// --- IdvScopesField ---
// Custom form-controlled scopes selector. The optional-scope checkboxes are DYNAMIC: the caller
// passes `supportedScopes` straight from the tenant's OIDC discovery doc (`scopes_supported`
// minus the always-required `openid`), and every one renders unchecked by default — the admin
// opts in. `openid` is rendered separately as a locked, checked row and is never in this list.
// Value is the list of selected optional scopes (openid is implied by the backend, never sent
// from this control).
interface IdvScopesFieldProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  // Optional scopes the tenant advertises (openid already excluded by the hook).
  supportedScopes?: string[];
}

const IdvScopesField = ({ value = [], onChange, supportedScopes = [] }: IdvScopesFieldProps) => {
  const { t } = useTranslation();

  // Render the advertised scopes plus any already-selected scope not in the current list — on
  // Edit, a connection may hold a scope the tenant no longer advertises; it must still show
  // (checked) rather than vanish from the UI while remaining in the payload.
  const scopesToRender = [...supportedScopes, ...value.filter(s => !supportedScopes.includes(s))];

  const toggle = (scope: string) => {
    onChange?.(value.includes(scope) ? value.filter(s => s !== scope) : [...value, scope]);
  };

  // Title/description come from i18n when we ship copy for the scope; otherwise the title falls
  // back to a humanized id and the description is omitted (no raw key rendered).
  const scopeControl = (scope: string) => {
    const title = t(`${TRANSLATION_ROOT}.modal.scope-${scope}`, {
      defaultValue: humanizeScope(scope),
    });
    const description = t(`${TRANSLATION_ROOT}.modal.scope-${scope}-description`, {
      defaultValue: '',
    });
    return (
      <Checkbox
        className="idv-scope-option"
        key={scope}
        checked={value.includes(scope)}
        onChange={() => toggle(scope)}
      >
        <span className="idv-scope-text">
          <span className="idv-scope-title">{title}</span>
          {description && <span className="idv-scope-description">{description}</span>}
        </span>
      </Checkbox>
    );
  };

  return (
    <>
      <p className="idv-section-description">{t(`${TRANSLATION_ROOT}.modal.scopes-description`)}</p>
      {/*
        Toggle rather than disable: when no optional scopes are selected the link
        selects all supported ones; otherwise it clears them. Matches Figma (28709-13590)
        and keeps the control always actionable (never a disabled dead-end). Only rendered
        when there is at least one optional scope to act on — with none (discovery omitted
        `scopes_supported`, the tenant supports only `openid`, or discovery failed) the link
        would be a no-op, so it is hidden and only the locked OpenID row shows.
      */}
      {scopesToRender.length > 0 && (
        <Button
          type="link"
          className="idv-clear-scopes-link"
          onClick={() => onChange?.(value.length === 0 ? [...scopesToRender] : [])}
        >
          <span className="btn-label">
            {t(
              value.length === 0
                ? `${TRANSLATION_ROOT}.modal.scopes-select-all`
                : `${TRANSLATION_ROOT}.modal.scopes-clear-all`,
            )}
          </span>
        </Button>
      )}
      <div className="idv-scopes-grid">
        <Checkbox className="idv-scope-option idv-scope-option-locked" checked disabled>
          <span className="idv-scope-text">
            <span className="idv-scope-title">{t(`${TRANSLATION_ROOT}.modal.scope-openid`)}</span>
            <span className="idv-scope-description">
              {t(`${TRANSLATION_ROOT}.modal.scope-openid-description`)}
            </span>
          </span>
        </Checkbox>
        {scopesToRender.map(scopeControl)}
      </div>
    </>
  );
};

// --- IdvRadioField ---
// Renders a radio group where each option has a bold label and a helper
// description beneath it, matching the Figma "Authentication" and
// "Identity assurance level" sections.
interface IdvRadioOption {
  value: string;
  labelKey: string;
  descriptionKey: string;
}

interface IdvRadioFieldProps {
  options: IdvRadioOption[];
  value?: string;
  onChange?: (value: string) => void;
}

const IdvRadioField = ({ options, value, onChange }: IdvRadioFieldProps) => {
  const { t } = useTranslation();
  return (
    <Radio.Group
      className="idv-radio-field"
      value={value}
      onChange={e => onChange?.(e.target.value)}
    >
      {options.map(opt => (
        // Title + description live INSIDE the radio label so the description starts exactly
        // where the label text does — aligned regardless of the radio circle's rendered size
        // (no magic indent). Mirrors the scope-checkbox layout.
        <ImprRadio className="idv-radio-option" value={opt.value} key={opt.value}>
          <span className="idv-radio-text">
            <span className="idv-radio-title">{t(opt.labelKey)}</span>
            <span className="idv-radio-description">{t(opt.descriptionKey)}</span>
          </span>
        </ImprRadio>
      ))}
    </Radio.Group>
  );
};

// --- IdvReadOnlyRadio ---
// A single selected-but-disabled radio option with its helper description, used
// to display an immutable choice (e.g. the auth method on the custom edit form).
const IdvReadOnlyRadio = ({ option }: { option?: IdvRadioOption }) => {
  const { t } = useTranslation();
  if (!option) {
    return null;
  }
  return (
    <div className="idv-radio-field idv-radio-field-readonly">
      <ImprRadio className="idv-radio-option" checked disabled>
        <span className="idv-radio-text">
          <span className="idv-radio-title">{t(option.labelKey)}</span>
          <span className="idv-radio-description">{t(option.descriptionKey)}</span>
        </span>
      </ImprRadio>
    </div>
  );
};

const AUTH_METHOD_OPTIONS: IdvRadioOption[] = [
  {
    value: IdvAuthMethod.CLIENT_SECRET_POST,
    labelKey: `${TRANSLATION_ROOT}.modal.auth-method-client-secret-post`,
    descriptionKey: `${TRANSLATION_ROOT}.modal.auth-method-client-secret-post-description`,
  },
  {
    value: IdvAuthMethod.PRIVATE_KEY_JWT,
    labelKey: `${TRANSLATION_ROOT}.modal.auth-method-private-key-jwt`,
    descriptionKey: `${TRANSLATION_ROOT}.modal.auth-method-private-key-jwt-description`,
  },
];

const IAL_LEVEL_OPTIONS: IdvRadioOption[] = [
  {
    value: IdvIalLevel.IAL1,
    labelKey: `${TRANSLATION_ROOT}.modal.ial-level-ial1`,
    descriptionKey: `${TRANSLATION_ROOT}.modal.ial-level-ial1-description`,
  },
  {
    value: IdvIalLevel.IAL2,
    labelKey: `${TRANSLATION_ROOT}.modal.ial-level-ial2`,
    descriptionKey: `${TRANSLATION_ROOT}.modal.ial-level-ial2-description`,
  },
];

export const makeHttpsUrlValidator = (errorKey: string, t: TFunction) => ({
  validator: (_: unknown, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    try {
      const url = new URL(value);
      if (url.protocol !== 'https:') {
        throw new Error();
      }
    } catch {
      return Promise.reject(new Error(t(errorKey)));
    }
    return Promise.resolve();
  },
});

export const makeRedirectUriFormatValidator = (t: TFunction) => ({
  validator: (_: unknown, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    // Accept any URI with a scheme (e.g. https://, http://, impr-mam://).
    // Backend enforces stricter validation; UI only rejects completely malformed input.
    if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(value)) {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.redirect-uri-invalid`)));
    }
    return Promise.resolve();
  },
});

export const makeRedirectUriDuplicateValidator = (t: TFunction, form: FormInstance) => ({
  validator: (_: unknown, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    const allUris: string[] = form.getFieldValue(FieldName.REDIRECT_URIS) ?? [];
    if (allUris.filter(u => u === value).length > 1) {
      return Promise.reject(new Error(t(`${TRANSLATION_ROOT}.validation.redirect-uri-duplicate`)));
    }
    return Promise.resolve();
  },
});

// --- IdvFormFooter ---

interface IdvFormFooterProps {
  selectedTemplate: IdvConnectionTemplate;
  editingConnection: IdvConnection | null;
  isLoading: boolean;
  onCancel: () => void;
}

export const IdvFormFooter = ({
  selectedTemplate,
  editingConnection,
  isLoading,
  onCancel,
}: IdvFormFooterProps) => {
  const { t } = useTranslation();
  const isEditMode = !!editingConnection;
  const isCustom =
    selectedTemplate === IdvConnectionTemplate.CUSTOM ||
    editingConnection?.templateType === IdvConnectionTemplate.CUSTOM;
  const isServiceNow =
    selectedTemplate === IdvConnectionTemplate.SERVICE_NOW ||
    editingConnection?.templateType === IdvConnectionTemplate.SERVICE_NOW;

  return (
    <div className="idv-form-button-container">
      <Button
        label={t('common.cancel')}
        onClick={onCancel}
        size="large"
        className="cancel-button"
      />
      <Form.Item shouldUpdate noStyle>
        {formInstance => {
          const vals = formInstance.getFieldsValue() as FormValues;
          const hasName = !!vals[FieldName.DISPLAY_NAME]?.trim();
          const hasUri = (vals[FieldName.REDIRECT_URIS] ?? []).some(Boolean);
          // ServiceNow always needs a JWKS URI; Custom needs one only for private_key_jwt.
          const authMethod = vals[FieldName.AUTH_METHOD];
          const needsJwks =
            isServiceNow || (isCustom && authMethod === IdvAuthMethod.PRIVATE_KEY_JWT);
          const hasJwks = needsJwks ? !!vals[FieldName.JWKS_URI] : true;
          const hasCustomFields =
            isCustom && !isEditMode ? !!authMethod && !!vals[FieldName.IAL_LEVEL] : true;
          const hasRequiredFields = hasName && hasUri && hasJwks && hasCustomFields;

          let isDirty = true;
          if (isEditMode && editingConnection) {
            const nameChanged =
              vals[FieldName.DISPLAY_NAME]?.trim() !== editingConnection.displayName;
            const currentUris = (vals[FieldName.REDIRECT_URIS] ?? [])
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b));
            const originalUris = [...(editingConnection.redirectUris ?? [])]
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b));
            const urisChanged =
              currentUris.length !== originalUris.length ||
              currentUris.some((u, i) => u !== originalUris[i]);
            const jwksChanged =
              (vals[FieldName.JWKS_URI] ?? '') !== (editingConnection.jwksUri ?? '');
            // Scopes and IAL are editable post-create for Custom connections.
            const currentScopes = [...(vals[FieldName.SCOPES] ?? [])]
              .sort((a, b) => a.localeCompare(b))
              .join(',');
            const originalScopes = [...(editingConnection.scope ?? [])]
              .sort((a, b) => a.localeCompare(b))
              .join(',');
            const scopesChanged = isCustom && currentScopes !== originalScopes;
            const ialChanged =
              isCustom && vals[FieldName.IAL_LEVEL] !== editingConnection.identityAssuranceLevel;
            isDirty =
              nameChanged ||
              urisChanged ||
              (needsJwks && jwksChanged) ||
              scopesChanged ||
              ialChanged;
          }

          const canSave = hasRequiredFields && isDirty;
          return (
            <Button
              size="large"
              type="primary"
              htmlType="submit"
              disabled={!canSave || isLoading}
              label={t('common.save')}
              aria-describedby={!canSave ? 'idv-save-hint' : undefined}
            />
          );
        }}
      </Form.Item>
      <span id="idv-save-hint" className="sr-only">
        {t(`${TRANSLATION_ROOT}.validation.save-hint`)}
      </span>
    </div>
  );
};

// --- IdvConnectionForm ---

interface IdvConnectionFormProps {
  form: FormInstance<FormValues>;
  editingConnection: IdvConnection | null;
  selectedTemplate: IdvConnectionTemplate;
  isLoading: boolean;
  onCancel: () => void;
  onFinish: (values: FormValues) => Promise<void>;
  // Optional scopes advertised by the tenant's OIDC discovery doc (openid already excluded),
  // rendered as the Custom form's scope checkboxes.
  supportedScopes?: string[];
}

export const IdvConnectionForm = ({
  form,
  editingConnection,
  selectedTemplate,
  isLoading,
  onCancel,
  onFinish,
  supportedScopes = [],
}: IdvConnectionFormProps) => {
  const { t } = useTranslation();
  const isEditMode = !!editingConnection;
  const isCustom =
    selectedTemplate === IdvConnectionTemplate.CUSTOM ||
    editingConnection?.templateType === IdvConnectionTemplate.CUSTOM;
  const showServiceNowAuth =
    selectedTemplate === IdvConnectionTemplate.SERVICE_NOW ||
    editingConnection?.templateType === IdvConnectionTemplate.SERVICE_NOW;
  let systemName = 'Epic';
  if (showServiceNowAuth) {
    systemName = 'ServiceNow';
  } else if (isCustom) {
    systemName = 'your system';
  }

  // Custom connections have no single owning system, so their read-only credential copy
  // uses "configure your integration" wording instead of "share with {systemName}".
  const clientIdDescription = isCustom
    ? t(`${TRANSLATION_ROOT}.modal.client-id-description-custom`)
    : t(`${TRANSLATION_ROOT}.modal.client-id-description`, { systemName });
  const endpointsDescription = isCustom
    ? t(`${TRANSLATION_ROOT}.modal.endpoints-description-custom`)
    : t(`${TRANSLATION_ROOT}.modal.endpoints-description`, { systemName });

  return (
    <ConfigProvider
      theme={{
        components: {
          Input: {
            colorBorder: '#999999',
            colorPrimaryHover: '#999999',
            borderRadius: 8,
            controlHeight: 48,
            controlHeightLG: 48,
          },
        },
      }}
    >
      <Form
        name="idvConnection"
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
      >
        <Form.Item
          label={t(`${TRANSLATION_ROOT}.modal.display-name-label`)}
          name={FieldName.DISPLAY_NAME}
          validateTrigger={['onBlur']}
          rules={[
            {
              required: true,
              whitespace: true,
              message: t(`${TRANSLATION_ROOT}.validation.display-name-required`),
            },
          ]}
        >
          <InputBox size="large" />
        </Form.Item>

        {isCustom && (
          <div className="idv-form-section">
            <div className="section-heading">{t(`${TRANSLATION_ROOT}.modal.scopes-label`)}</div>
            <Form.Item name={FieldName.SCOPES} noStyle>
              <IdvScopesField supportedScopes={supportedScopes} />
            </Form.Item>
          </div>
        )}

        {isCustom && !isEditMode && (
          <div className="idv-form-section">
            <div className="section-heading">
              {t(`${TRANSLATION_ROOT}.modal.auth-method-label`)}
            </div>
            <p className="idv-section-description">
              {t(`${TRANSLATION_ROOT}.modal.auth-method-description`)}
            </p>
            <Form.Item
              name={FieldName.AUTH_METHOD}
              rules={[
                {
                  required: true,
                  message: t(`${TRANSLATION_ROOT}.validation.auth-method-required`),
                },
              ]}
            >
              <IdvRadioField options={AUTH_METHOD_OPTIONS} />
            </Form.Item>
          </div>
        )}

        {/* Custom edit: auth method is immutable, shown read-only; JWKS URI stays editable. */}
        {isCustom && isEditMode && editingConnection?.tokenEndpointAuthMethod && (
          <div className="idv-form-section">
            <div className="section-heading">
              {t(`${TRANSLATION_ROOT}.modal.auth-method-label`)}
            </div>
            <p className="idv-section-description">
              {t(`${TRANSLATION_ROOT}.modal.auth-method-description`)}
            </p>
            <IdvReadOnlyRadio
              option={AUTH_METHOD_OPTIONS.find(
                o => o.value === editingConnection.tokenEndpointAuthMethod,
              )}
            />
            {editingConnection.tokenEndpointAuthMethod === IdvAuthMethod.PRIVATE_KEY_JWT && (
              <Form.Item
                className="idv-jwks-uri-nested"
                label={t(`${TRANSLATION_ROOT}.modal.jwks-uri-label`)}
                name={FieldName.JWKS_URI}
                validateTrigger={['onBlur']}
                rules={[
                  {
                    required: true,
                    message: t(`${TRANSLATION_ROOT}.validation.jwks-uri-required`),
                  },
                  makeHttpsUrlValidator(`${TRANSLATION_ROOT}.validation.jwks-uri-invalid`, t),
                ]}
              >
                <InputBox
                  size="large"
                  placeholder={t(`${TRANSLATION_ROOT}.modal.jwks-uri-placeholder`)}
                />
              </Form.Item>
            )}
          </div>
        )}

        {/* JWKS URI: ServiceNow always; Custom (create) only for private_key_jwt. */}
        {showServiceNowAuth && (
          <div className="idv-form-section">
            <div className="section-heading">
              {t(`${TRANSLATION_ROOT}.modal.authentication-label`)}
            </div>
            <p className="idv-section-description">
              {t(`${TRANSLATION_ROOT}.modal.authentication-description`)}
            </p>
            <Form.Item
              label={t(`${TRANSLATION_ROOT}.modal.jwks-uri-label`)}
              name={FieldName.JWKS_URI}
              validateTrigger={['onBlur']}
              rules={[
                { required: true, message: t(`${TRANSLATION_ROOT}.validation.jwks-uri-required`) },
                makeHttpsUrlValidator(`${TRANSLATION_ROOT}.validation.jwks-uri-invalid`, t),
              ]}
            >
              <InputBox
                size="large"
                placeholder={t(`${TRANSLATION_ROOT}.modal.jwks-uri-placeholder`)}
              />
            </Form.Item>
          </div>
        )}

        {isCustom && !isEditMode && (
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev[FieldName.AUTH_METHOD] !== cur[FieldName.AUTH_METHOD]}
          >
            {formInstance =>
              formInstance.getFieldValue(FieldName.AUTH_METHOD) ===
              IdvAuthMethod.PRIVATE_KEY_JWT ? (
                <Form.Item
                  className="idv-jwks-uri-nested"
                  label={t(`${TRANSLATION_ROOT}.modal.jwks-uri-label`)}
                  name={FieldName.JWKS_URI}
                  validateTrigger={['onBlur']}
                  rules={[
                    {
                      required: true,
                      message: t(`${TRANSLATION_ROOT}.validation.jwks-uri-required`),
                    },
                    makeHttpsUrlValidator(`${TRANSLATION_ROOT}.validation.jwks-uri-invalid`, t),
                  ]}
                >
                  <InputBox
                    size="large"
                    placeholder={t(`${TRANSLATION_ROOT}.modal.jwks-uri-placeholder`)}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        )}

        {isCustom && (
          <div className="idv-form-section">
            <div className="section-heading">{t(`${TRANSLATION_ROOT}.modal.ial-level-label`)}</div>
            <p className="idv-section-description">
              {t(`${TRANSLATION_ROOT}.modal.ial-level-description`)}
            </p>
            <Form.Item
              name={FieldName.IAL_LEVEL}
              rules={[
                { required: true, message: t(`${TRANSLATION_ROOT}.validation.ial-level-required`) },
              ]}
            >
              <IdvRadioField options={IAL_LEVEL_OPTIONS} />
            </Form.Item>
          </div>
        )}

        <div className="idv-form-section idv-redirect-uri-section">
          <div className="section-heading">{t(`${TRANSLATION_ROOT}.modal.redirect-uri-label`)}</div>
          <p className="idv-section-description">
            {t(`${TRANSLATION_ROOT}.modal.redirect-uri-description`)}
          </p>
          <Form.List name={FieldName.REDIRECT_URIS}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  // Spacing between rows comes from the section's 16px flex gap (see
                  // `.idv-redirect-uri-section`), so no per-item margin is needed.
                  <Form.Item key={field.key} label="Redirect URI">
                    <div className="idv-redirect-uri-row">
                      <Form.Item
                        name={field.name}
                        noStyle
                        validateTrigger={['onBlur']}
                        rules={[
                          {
                            required: true,
                            message:
                              fields.length > 1
                                ? t(`${TRANSLATION_ROOT}.validation.redirect-uri-required-another`)
                                : t(`${TRANSLATION_ROOT}.validation.redirect-uri-required`),
                          },
                          makeRedirectUriFormatValidator(t),
                          makeRedirectUriDuplicateValidator(t, form),
                        ]}
                      >
                        <InputBox size="large" placeholder="https://example.com/callback" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <ImprTooltip
                          title={t('common.remove')}
                          placement="bottom"
                          styles={{ body: { borderRadius: 8 } }}
                        >
                          <Button
                            type="text"
                            aria-label={`remove redirect URI ${index + 1}`}
                            onClick={() => {
                              remove(field.name);
                              form.setFields([
                                { name: [FieldName.REDIRECT_URIS, field.name], errors: [] },
                              ]);
                            }}
                            icon={<Trash2 size={24} />}
                            className="idv-redirect-uri-delete-btn"
                            size="middle"
                          />
                        </ImprTooltip>
                      )}
                    </div>
                  </Form.Item>
                ))}
                <Form.Item noStyle>
                  <Button
                    type="link"
                    onClick={() => {
                      add('');
                    }}
                    className="add-redirect-uri-link"
                    icon={<span>+</span>}
                  >
                    <span className="btn-label">
                      {t(`${TRANSLATION_ROOT}.modal.add-redirect-uri`).replace('+ ', '')}
                    </span>
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </div>

        {isEditMode && editingConnection && (
          <div className="credentials-section-readonly">
            <div className="section-header">
              <h4>{t(`${TRANSLATION_ROOT}.modal.client-id-label`)}</h4>
              <p className="section-description">{clientIdDescription}</p>
            </div>
            <div className="input-with-copy">
              <InputBox value={editingConnection.clientId} readOnly size="large" />
              <CopyButton
                aria-label={t(`${TRANSLATION_ROOT}.modal.client-id-copy`)}
                textToCopy={editingConnection.clientId}
                dynamicLabel={{ initialLabel: '', labelAfterClick: '', dynamicLabelDuration: 2000 }}
              />
            </div>
          </div>
        )}

        {isEditMode && editingConnection && (
          <div className="credentials-section-readonly">
            <div className="section-header">
              <h4>{t(`${TRANSLATION_ROOT}.modal.endpoints-label`)}</h4>
              <p className="section-description">{endpointsDescription}</p>
            </div>
            <div className="endpoint-item">
              <span>{t(`${TRANSLATION_ROOT}.modal.authorization-endpoint-label`)}</span>
              <div className="input-with-copy">
                <InputBox
                  value={editingConnection.authorizationEndpointUrl || ''}
                  readOnly
                  size="large"
                />
                <CopyButton
                  aria-label="Copy authorization endpoint"
                  textToCopy={editingConnection.authorizationEndpointUrl || ''}
                  dynamicLabel={{
                    initialLabel: '',
                    labelAfterClick: '',
                    dynamicLabelDuration: 2000,
                  }}
                />
              </div>
            </div>
            <div className="endpoint-item">
              <span>{t(`${TRANSLATION_ROOT}.modal.token-endpoint-label`)}</span>
              <div className="input-with-copy">
                <InputBox value={editingConnection.tokenEndpointUrl || ''} readOnly size="large" />
                <CopyButton
                  aria-label="Copy token endpoint"
                  textToCopy={editingConnection.tokenEndpointUrl || ''}
                  dynamicLabel={{
                    initialLabel: '',
                    labelAfterClick: '',
                    dynamicLabelDuration: 2000,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        <IdvFormFooter
          selectedTemplate={selectedTemplate}
          editingConnection={editingConnection}
          isLoading={isLoading}
          onCancel={onCancel}
        />
      </Form>
    </ConfigProvider>
  );
};

// --- IdvSuccessState ---

interface IdvSuccessStateProps {
  connection: IdvConnection;
  clientSecret?: string;
  showClientSecret: boolean;
  onShowClientSecretChange: (v: boolean) => void;
  onDone: () => void;
  onSecretCopied: () => void;
  onSecretKeyboardCopied: () => void;
  secretKeyboardCopied?: boolean;
  onSecretKeyboardCopiedReset?: () => void;
}

export const IdvSuccessState = ({
  connection,
  clientSecret,
  showClientSecret,
  onShowClientSecretChange,
  onDone,
  onSecretCopied,
  onSecretKeyboardCopied,
  secretKeyboardCopied,
  onSecretKeyboardCopiedReset,
}: IdvSuccessStateProps) => {
  const { t } = useTranslation();
  const isServiceNow = connection.templateType === IdvConnectionTemplate.SERVICE_NOW;
  const isCustom = connection.templateType === IdvConnectionTemplate.CUSTOM;
  const systemName = isServiceNow ? 'ServiceNow' : 'Epic';

  // Custom connections have no single owning system, so their credential copy uses
  // "configure your integration" wording instead of the "share with {systemName}" copy.
  const clientSecretDescription = isCustom
    ? t(`${TRANSLATION_ROOT}.modal.client-secret-description-custom`)
    : t(`${TRANSLATION_ROOT}.modal.client-secret-description`, { systemName });
  const clientIdDescription = isCustom
    ? t(`${TRANSLATION_ROOT}.modal.client-id-description-custom`)
    : t(`${TRANSLATION_ROOT}.modal.client-id-description`, { systemName });
  const endpointsDescription = isCustom
    ? t(`${TRANSLATION_ROOT}.modal.endpoints-description-custom`)
    : t(`${TRANSLATION_ROOT}.modal.endpoints-description`, { systemName });

  // `openid` is always required for Custom connections and is shown as a locked,
  // pre-checked control in the form, but it is never part of connection.scope (the
  // form only controls the optional scopes). Prefix its summary label here so the
  // success summary matches the form's UI contract and never renders empty. Uses the
  // `-summary` label ("OpenID") rather than the form's "OpenID — Required" so it reads
  // cleanly alongside the optional scopes. Non-custom summaries aren't shown, but stay
  // openid-free to reflect the raw stored scopes.
  const optionalScopeLabels = (connection.scope ?? []).map(scope =>
    t(`${TRANSLATION_ROOT}.modal.scope-${scope}`),
  );
  const scopesSummary = (
    isCustom
      ? [t(`${TRANSLATION_ROOT}.modal.scope-openid-summary`), ...optionalScopeLabels]
      : optionalScopeLabels
  ).join(', ');
  const authMethodShortKey: Record<IdvAuthMethod, string> = {
    [IdvAuthMethod.CLIENT_SECRET_POST]: `${TRANSLATION_ROOT}.modal.auth-method-client-secret-post-short`,
    [IdvAuthMethod.PRIVATE_KEY_JWT]: `${TRANSLATION_ROOT}.modal.auth-method-private-key-jwt-short`,
  };
  const authMethodSummary = connection.tokenEndpointAuthMethod
    ? t(authMethodShortKey[connection.tokenEndpointAuthMethod])
    : '';

  return (
    <ConfigProvider
      wave={{ disabled: true }}
      theme={{
        components: {
          Input: {
            colorBorder: '#999999',
            colorPrimaryHover: '#999999',
            borderRadius: 8,
            controlHeight: 48,
            controlHeightLG: 48,
          },
        },
      }}
    >
      <div className="idv-success-state-new">
        <Alert
          message={t(`${TRANSLATION_ROOT}.modal.success-banner`)}
          type="success"
          showIcon
          icon={
            <svg
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Success"
            >
              <title>Success</title>
              <circle cx="11" cy="11" r="10" fill="white" stroke="#30961F" strokeWidth="2" />
              <path
                d="M7.5 11L10 14L15.5 9"
                stroke="#30961F"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          className="success-alert"
        />

        <div className="connection-summary">
          <div className="summary-row">
            <span className="summary-label">
              {t(`${TRANSLATION_ROOT}.modal.display-name-label`)}
            </span>
            <span className="summary-value">{connection.displayName}</span>
          </div>
          <div className="summary-row">
            <span className="summary-label">
              {t(`${TRANSLATION_ROOT}.modal.integration-type-label`)}
            </span>
            <span className="summary-value">
              {t(`${TRANSLATION_ROOT}.modal.integration-type-idv`)}
            </span>
          </div>
          <div className="summary-row">
            <span className="summary-label">
              {t(
                connection.redirectUris.length > 1
                  ? `${TRANSLATION_ROOT}.modal.redirect-uri-label`
                  : `${TRANSLATION_ROOT}.modal.redirect-url-label`,
              )}
            </span>
            {/* A connection may hold several redirect URIs; list them one per line. */}
            <ul className="summary-value">
              {connection.redirectUris.map(uri => (
                <li key={uri}>{uri}</li>
              ))}
            </ul>
          </div>
          {isCustom && (
            <>
              <div className="summary-row">
                <span className="summary-label">{t(`${TRANSLATION_ROOT}.modal.scopes-label`)}</span>
                <span className="summary-value">{scopesSummary}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">
                  {t(`${TRANSLATION_ROOT}.modal.auth-method-label`)}
                </span>
                <span className="summary-value">{authMethodSummary}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">
                  {t(`${TRANSLATION_ROOT}.modal.ial-level-label`)}
                </span>
                <span className="summary-value">
                  {connection.identityAssuranceLevel === IdvIalLevel.IAL1
                    ? t(`${TRANSLATION_ROOT}.modal.ial-level-ial1`)
                    : t(`${TRANSLATION_ROOT}.modal.ial-level-ial2`)}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="success-divider" />

        {clientSecret && (
          <>
            <div className="credentials-section secret-card">
              <div className="section-header">
                <h4>{t(`${TRANSLATION_ROOT}.modal.client-secret-label`)}</h4>
                <p className="section-description">{clientSecretDescription}</p>
              </div>
              <div className="input-with-copy secret-input">
                <Input
                  value={showClientSecret ? clientSecret : '*'.repeat(clientSecret.length)}
                  readOnly
                  size="small"
                  suffix={
                    <button
                      type="button"
                      style={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                      }}
                      onClick={() => onShowClientSecretChange(!showClientSecret)}
                      aria-label={
                        showClientSecret
                          ? t(`${TRANSLATION_ROOT}.modal.client-secret-hide`)
                          : t(`${TRANSLATION_ROOT}.modal.client-secret-show`)
                      }
                    >
                      {showClientSecret ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  }
                  onKeyDown={e => {
                    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                      e.preventDefault();
                      copyToClipboard(clientSecret);
                      onSecretCopied();
                      onSecretKeyboardCopied();
                    }
                  }}
                />
                <CopyButton
                  aria-label={t(`${TRANSLATION_ROOT}.modal.client-secret-copy`)}
                  textToCopy={clientSecret}
                  dynamicLabel={{
                    initialLabel: '',
                    labelAfterClick: '',
                    dynamicLabelDuration: 2000,
                  }}
                  externalCopied={secretKeyboardCopied}
                  onExternalCopiedReset={onSecretKeyboardCopiedReset}
                  onClick={() => onSecretCopied()}
                />
              </div>
              <Alert
                message={t(`${TRANSLATION_ROOT}.modal.client-secret-warning`)}
                type="warning"
                showIcon
                icon={<AlertTriangle size={22} />}
                className="secret-warning"
              />
            </div>
            <div className="success-divider" />
          </>
        )}

        <div className="credentials-section">
          <div className="section-header">
            <h4>{t(`${TRANSLATION_ROOT}.modal.client-id-label`)}</h4>
            <p className="section-description">{clientIdDescription}</p>
          </div>
          <div className="input-with-copy">
            <InputBox value={connection.clientId} readOnly size="large" />
            <CopyButton
              aria-label={t(`${TRANSLATION_ROOT}.modal.client-id-copy`)}
              textToCopy={connection.clientId}
              dynamicLabel={{ initialLabel: '', labelAfterClick: '', dynamicLabelDuration: 2000 }}
            />
          </div>
        </div>

        <div className="credentials-section">
          <div className="section-header">
            <h4>{t(`${TRANSLATION_ROOT}.modal.endpoints-label`)}</h4>
            <p className="section-description">{endpointsDescription}</p>
          </div>
          <div className="endpoint-item">
            <span>{t(`${TRANSLATION_ROOT}.modal.authorization-endpoint-label`)}</span>
            <div className="input-with-copy">
              <InputBox value={connection.authorizationEndpointUrl || ''} readOnly size="large" />
              <CopyButton
                aria-label="Copy authorization endpoint"
                textToCopy={connection.authorizationEndpointUrl || ''}
                dynamicLabel={{ initialLabel: '', labelAfterClick: '', dynamicLabelDuration: 2000 }}
              />
            </div>
          </div>
          <div className="endpoint-item">
            <span>{t(`${TRANSLATION_ROOT}.modal.token-endpoint-label`)}</span>
            <div className="input-with-copy">
              <InputBox value={connection.tokenEndpointUrl || ''} readOnly size="large" />
              <CopyButton
                aria-label="Copy token endpoint"
                textToCopy={connection.tokenEndpointUrl || ''}
                dynamicLabel={{ initialLabel: '', labelAfterClick: '', dynamicLabelDuration: 2000 }}
              />
            </div>
          </div>
        </div>

        <div className="success-footer">
          <Button
            size="large"
            type="primary"
            onClick={onDone}
            label={t(`${TRANSLATION_ROOT}.modal.done-button`)}
          />
        </div>
      </div>
    </ConfigProvider>
  );
};
